import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-keys';

interface RPCRow {
  outcome: string;
  current_count: number;
  previous_count: number;
  current_percent: number;
}

export interface BreakdownRow {
  segment: string;
  current_count: number;
  previous_count: number;
  current_percent: number;
}

export interface BreakdownResult {
  kind: 'breakdown';
  rows: BreakdownRow[];
  total_current: number;
  total_previous: number | null;
  delta_pct: number | null;
  isLoading: boolean;
  error: Error | null;
}

interface UseCallOutcomesBreakdownArgs {
  workspaceId: string;
  from: Date;
  to: Date;
  compareFrom: Date | null;
  compareTo: Date | null;
  userFilter: string | null;
  tz: string;
  enabled?: boolean;
}

function deltaPct(current: number, previous: number | null): number | null {
  if (previous === null) return null;
  if (previous === 0 && current === 0) return 0;
  if (previous === 0) return null;
  return Math.round(((current - previous) / previous) * 1000) / 10;
}

export function useCallOutcomesBreakdown(opts: UseCallOutcomesBreakdownArgs): BreakdownResult {
  const fromIso = opts.from.toISOString();
  const toIso = opts.to.toISOString();
  const comparisonEnabled = opts.compareFrom !== null && opts.compareTo !== null;

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.analytics.series(
      opts.workspaceId, 'call_outcomes', fromIso, toIso, 'flat', opts.userFilter,
    ),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_call_outcomes_breakdown', {
        p_workspace_id: opts.workspaceId,
        p_from: fromIso,
        p_to: toIso,
        p_compare_from: opts.compareFrom?.toISOString() ?? null,
        p_compare_to: opts.compareTo?.toISOString() ?? null,
        p_user_filter: opts.userFilter,
        p_tz: opts.tz,
      });
      if (error) throw error;
      return (data ?? []) as RPCRow[];
    },
    enabled: opts.enabled ?? true,
    staleTime: 60_000,
  });

  const rows: BreakdownRow[] = (data ?? []).map((r) => ({
    segment: r.outcome,
    current_count: r.current_count,
    previous_count: r.previous_count,
    current_percent: Number(r.current_percent),
  }));

  const totalCurrent = rows.reduce((s, r) => s + r.current_count, 0);
  const totalPrevious = comparisonEnabled
    ? rows.reduce((s, r) => s + r.previous_count, 0)
    : null;

  return {
    kind: 'breakdown',
    rows,
    total_current: totalCurrent,
    total_previous: totalPrevious,
    delta_pct: deltaPct(totalCurrent, totalPrevious),
    isLoading,
    error: (error as Error) ?? null,
  };
}
